import { buildFieldAnchors } from '../shared/previewFieldAnchors';
import { CORRECTION_ROWS, cellKeys } from './nidCorrectionFormEngine';

/**
 * Type and layout metrics lifted off the official Form-2 PDF (জাতীয়
 * পরিচয়পত্র বা তথ্য-উপাত্ত সংশোধনের আবেদন, প্রবিধান ৪).
 *
 * The government file is a 300 dpi scan with an invisible OCR layer over it, so
 * every number here was measured off a 4× render of that scan (page
 * 595.2 × 841.7 pt, so 1 pt = 1 px here): row baselines and label x positions
 * from the ink extents, the table grid from the printed rules, the leader spans
 * from where the dot runs actually start and stop. The scan is very slightly
 * skewed, so rows that print as one line on the original — the two signature
 * columns, the table separators — are normalised onto a single baseline here.
 *
 * The preview re-typesets the form at these coordinates rather than stamping
 * the scan, so a printed page lines up with the real form.
 */

/** Page box — A4 at 72 dpi, same as the original. */
export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/**
 * Font sizes (px = pt) and weights.
 *
 * These are not guesses at what the original "looks like": Kalpurush sets about
 * 1.36× wider than the face the government form was printed in, so each size
 * here is solved from a measured run of that form — its ink width divided by
 * Kalpurush's advance per pixel for the same words. That is what keeps the
 * label ends, colon stops and leader starts below on their measured x, and it
 * is why the body text is small: Form-2 is dense print, unlike the roomier
 * Form-13 the voter migration tool reproduces.
 */
export const TYPE = {
    title: { size: 9.6, weight: 400 },
    rule: { size: 8.1, weight: 400 },
    mainTitle: { size: 9.1, weight: 400 },
    body: { size: 8.1, weight: 400 },
    tableHead: { size: 9, weight: 400 },
    tableLabel: { size: 8.4, weight: 400 },
    receiptHead: { size: 8.8, weight: 400 },
    /** Page 2 runs the whole rulebook down one column, so it sets tighter. */
    instruction: { size: 7.9, weight: 400 },
};

/** Text sits in a 1.5 em line box, so dots and descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/**
 * Kalpurush's ascent is 1 em and its descent 0.4 em, so in that line box the
 * baseline lands 1.05 em below the top — the number that turns a measured
 * baseline into a CSS `top`.
 */
export const BASELINE_RATIO = (LINE_BOX - 1.4) / 2 + 1;

/**
 * The leaders are real full-stops in the form's own font — 1.67 pt of ink every
 * 3.25 pt — not a CSS border. Kalpurush's period advances 0.25 em, so the run
 * needs a little tracking on top.
 */
export const LEADER = {
    char: '.',
    /** Enough dots to overflow the longest line; each run is clipped. */
    count: 260,
    letterSpacing: 3.25 - TYPE.body.size * 0.25,
};

/**
 * The typed answers are not part of the printed form, so they do not have to
 * match its face: they print larger and bolder than the labels to stay legible
 * on a photocopy. A value that would overrun its blank shrinks down toward
 * `minSize` instead of being clipped.
 */
export const VALUE_TYPE = { size: 10.6, minSize: 7.6, weight: 400 };

/** The same, one size down, for the answers that print inside table cells. */
export const CELL_VALUE_TYPE = { size: 8.8, minSize: 6.4, weight: 400 };

/**
 * Typed values sit above the dots, the way handwriting does — far enough that a
 * Latin descender (g, j, p, q, y) clears the leader instead of sitting on it.
 */
export const VALUE_LIFT = 5;

/** …and start a few points in from the label, not hard against it. */
export const VALUE_INSET = 6;

/** The item numbers and the sub-item letters print in the left margin. */
export const ITEM_X = 97;
export const SUB_ITEM_X = 99;

/** Both `নাম` / `জাতীয় পরিচিতি নম্বর` blocks hang their colon on one tab stop. */
export const NAME_NID_COLON_X = 200;

/** Page 1 — the headings and fixed lines above the table. */
export const PAGE1_TEXT = {
    title: { y: 91, center: 317.5 },
    rule: { y: 102.5, center: 317.5 },
    mainTitle: { y: 114, center: 318 },
    /** ক্রমিক নম্বর : ……… (অফিস কর্তৃক পূরণীয়) — the office fills this one in. */
    serial: { y: 131, labelRight: 416, colonX: 418.5, from: 424, to: 470, noteX: 474 },
    holderHead: { y: 140.5, x: ITEM_X },
    guardianHead: { y: 192, x: ITEM_X },
    tableHead: { y: 243.5, x: ITEM_X },
};

/**
 * The four dotted blanks above the table: `(ক) নাম` and
 * `(খ) জাতীয় পরিচিতি নম্বর (NID)`, once for the card holder and once for the
 * legal guardian.
 */
export const PAGE1_FIELDS = [
    { y: 157, x: SUB_ITEM_X, label: 'holderNameLabel', from: 205, to: 497, value: 'holderName' },
    { y: 175, x: SUB_ITEM_X, label: 'holderNidLabel', from: 205, to: 497, value: 'holderNid' },
    { y: 207, x: SUB_ITEM_X, label: 'guardianNameLabel', from: 205, to: 497, value: 'guardianName' },
    { y: 225, x: SUB_ITEM_X, label: 'guardianNidLabel', from: 205, to: 497, value: 'guardianNid' },
];

/**
 * Item ৩'s table. The four column rules were read straight off the scan; the
 * nine body rows divide the space between the header rule and the foot rule
 * evenly, which is how the original prints them (22.6 pt apart, to within the
 * scan's own skew).
 */
export const TABLE = {
    /** Column rules, left to right — four columns, five lines. */
    cols: [102.3, 188.6, 335, 441.9, 532.5],
    top: 250.6,
    headBottom: 275.3,
    bottom: 478.4,
    rowCount: 9,
    /** The header sets two lines in column 2 and one in columns 1, 3 and 4. */
    headLine1Y: 260.5,
    headLine2Y: 272.5,
    /** Row labels are indented from the column rule, not centred. */
    labelX: 116,
    /** Where a row's label baseline sits inside its row box. */
    labelBaselineOffset: 15.2,
    /** Answers keep clear of the rules on both sides. */
    cellPadX: 4,
};

export const TABLE_ROW_HEIGHT = (TABLE.bottom - TABLE.headBottom) / TABLE.rowCount;

/**
 * Items ৪-৬. None of them prints a dotted leader — the original just leaves the
 * rest of the line blank — so the answer starts after the label and runs to the
 * right margin.
 */
export const PAGE1_TAIL_FIELDS = [
    { y: 487, x: ITEM_X, label: 'feeAmountLabel', from: 445, to: 572, value: 'feeAmount' },
    { y: 502, x: ITEM_X, label: 'feeReceiptLabel', from: 221, to: 572, value: 'feeReceipt' },
    { y: 519, x: ITEM_X, label: 'attachedDocsLabel', from: 246, to: 572, value: 'attachedDocs' },
];

/**
 * The two signature columns. On the scan the right column sits ~2 pt lower than
 * the left one; that is skew, not design, so both print on one baseline here.
 */
export const SIGNATURE = {
    dashY: 535,
    captionY: 547,
    left: { x: 97, dashFrom: 98, dashTo: 178, valueFrom: 168, valueTo: 352 },
    right: { x: 368, dashFrom: 368, dashTo: 446, valueFrom: 440, valueTo: 580 },
    /**
      * Line spacing for the one row that is allowed to wrap. Kalpurush draws
      * 0.94 em above the baseline and 0.42 below it, so a line box shorter than
      * 1.36 clips the ink — 1.2 shaved the matra off the first line and the
      * descenders off the last. This is the same 1.45 the instruction list uses.
      */
    lineHeight: 1.45,
    rows: [
        { y: 559.5, label: 'signName', left: 'guardianSignName', right: 'applicantSignName' },
        // A column this narrow holds about 132 pt, and a real address needs
        // roughly twice that even at the smallest size an answer may take — so
        // this row gets two lines and the rows under it start one line lower.
        { y: 571, label: 'signAddress', left: 'guardianSignAddress', right: 'applicantSignAddress', maxLines: 2 },
        // Far enough below the address for its second line to finish first,
        // even when the wider column fits that line at the full value size.
        { y: 602, label: 'signMobile', left: 'guardianSignMobile', right: 'applicantSignMobile' },
        { y: 613.5, label: 'signEmail', left: 'guardianSignEmail', right: 'applicantSignEmail' },
    ],
};

/** অংশ - খ (ফরম-২) — the receipt the office tears off, so nothing here fills in. */
export const PAGE1_RECEIPT = {
    // Twenty-one points lower than the scan, to clear the signature block's
    // two-line address row. Page 1 ends at 771 of 842, so the room was there.
    separator: { y: 622, from: 145, to: 489 },
    part: { y: 635, center: 292 },
    title: { y: 647.5, center: 285 },
    rule: { y: 661.5, center: 285.5 },
    serial: { y: 675, labelRight: 478, colonX: 481, from: 486, to: 536 },
    colonX: 316,
    rows: [
        { y: 686, x: 119, label: 'receiptApplicant' },
        { y: 697.5, x: 119, label: 'receiptGuardian', noColon: true },
        { y: 709, x: 132, label: 'receiptGuardianCont' },
        { y: 720, x: 119, label: 'receiptDate' },
        { y: 732, x: 119, label: 'receiptNextDate' },
    ],
    seal: { y: 758, x: 168 },
    officerDash: { y: 757, from: 392, to: 460 },
    officerCaption: { y: 770.5, center: 425.5 },
};

/** Page 2 — আবেদনকারীর জন্য নির্দেশাবলী, then the অংশ-ক receipt. */
export const PAGE2_TEXT = {
    title: { y: 88, center: 313.5 },
    /**
     * The instructions are the one part of either page that is set as running
     * text rather than placed line by line: the original wraps and justifies
     * them inside this column, and re-flowing them keeps the Bengali from
     * breaking in places the original never would.
     */
    instructions: { top: 100, left: 92, width: 442, gutter: 17, lineHeight: 1.45, paragraphGap: 1.6 },
};

/** অংশ-ক (ফরম-২) — প্রাপ্তিস্বীকার রশিদ, also office-filled. */
export const PAGE2_RECEIPT = {
    separator: { y: 601, from: 170, to: 453 },
    part: { y: 614, center: 311.5 },
    title: { y: 626.5, center: 311.5 },
    rule: { y: 638.5, center: 311 },
    colonX: 279,
    leader: { from: 284, to: 493 },
    rows: [
        { y: 649, x: 92, label: 'receiptApplicant', leader: true },
        { y: 661, x: 92, label: 'receiptGuardian' },
        { y: 673, x: 105, label: 'receiptGuardianCont', leader: true },
        { y: 685, x: 92, label: 'receiptDate', leader: true },
        { y: 697, x: 92, label: 'receiptBiometric' },
        { y: 709, x: 105, label: 'receiptBiometricCont', leader: true },
    ],
    seal: { y: 721, x: 107 },
    officerDash: { y: 731, from: 375, to: 461 },
    officerCaption: { y: 745, center: 418 },
};

/**
 * Where each answer prints, so the preview can follow the field being typed.
 *
 * This form does not lay out as one flat list of rows: items ১-২ and ৪-৬ are
 * dotted blanks, item ৩ is a nine-row table, and the signature block is two
 * columns of the same four questions. Each shape contributes its own anchors.
 */
export const FIELD_ANCHORS = (() => {
    const anchors = buildFieldAnchors([{ rows: [...PAGE1_FIELDS, ...PAGE1_TAIL_FIELDS], combs: [] }]);
    CORRECTION_ROWS.forEach((row, index) => {
        const y = TABLE.headBottom + (index + 1) * TABLE_ROW_HEIGHT;
        const keys = cellKeys(row.key);
        for (const key of [keys.current, keys.corrected, keys.docs]) anchors[key] = { page: 0, y };
    });
    for (const row of SIGNATURE.rows) {
        anchors[row.left] = { page: 0, y: row.y };
        anchors[row.right] = { page: 0, y: row.y };
    }
    return anchors;
})();
